import { useState, useEffect } from "react";
import { X, Lightning, TrendUp, TrendDown, Shield, Check } from "@phosphor-icons/react";

const LOT_PRESETS = [0.01, 0.03, 0.05, 0.10, 0.50, 1.00];

export default function OrderModal({ isOpen, onClose, onConfirm, initialCommand = "buy_order", selectedCount = 1, busy = false }) {
  const [command, setCommand] = useState(initialCommand);
  const [hedgeSide, setHedgeSide] = useState("auto"); // "auto" | "buy" | "sell"
  const [lot, setLot] = useState("0.03");
  const [slPips, setSlPips] = useState("20");
  const [tpPips, setTpPips] = useState("40");
  const [trailingSlPips, setTrailingSlPips] = useState("0");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCommand(initialCommand);
      setErr("");
    }
  }, [isOpen, initialCommand]);

  if (!isOpen) return null;

  const isHedge = command === "hedge_custom";
  const isBuy = command === "buy_order";
  const isSell = command === "sell_order";

  const handleSubmit = (e) => {
    e.preventDefault();
    setErr("");
    const parsedLot = parseFloat(lot);
    if (isNaN(parsedLot) || parsedLot <= 0 || parsedLot > 100) {
      setErr("Lot size must be a number between 0.01 and 100.0");
      return;
    }

    const parsedSl = slPips !== "" ? parseFloat(slPips) : 0;
    if (isNaN(parsedSl) || parsedSl < 0) {
      setErr("Stop Loss must be a non-negative number of pips");
      return;
    }

    const parsedTp = tpPips !== "" ? parseFloat(tpPips) : 0;
    if (isNaN(parsedTp) || parsedTp < 0) {
      setErr("Take Profit must be a non-negative number of pips");
      return;
    }

    const parsedTrailing = trailingSlPips !== "" ? parseFloat(trailingSlPips) : 0;
    if (isNaN(parsedTrailing) || parsedTrailing < 0) {
      setErr("Trailing SL must be a non-negative number of pips");
      return;
    }

    const params = {
      lot: parsedLot,
      sl_pips: parsedSl,
      tp_pips: parsedTp,
      trailing_sl_pips: parsedTrailing,
    };

    if (isHedge) {
      params.side = hedgeSide;
      params.value = parsedLot;
    }

    let label = "";
    if (isHedge) {
      const sideText = hedgeSide === "buy" ? "Buy Hedge" : hedgeSide === "sell" ? "Sell Hedge" : "Auto Hedge";
      label = `Custom Lot ${sideText} (${parsedLot} Lot${parsedLot === 1 ? "" : "s"})`;
    } else if (isBuy) {
      label = `Buy Order (${parsedLot} Lot${parsedLot === 1 ? "" : "s"})`;
    } else if (isSell) {
      label = `Sell Order (${parsedLot} Lot${parsedLot === 1 ? "" : "s"})`;
    } else {
      label = `Custom Order (${parsedLot} Lot${parsedLot === 1 ? "" : "s"})`;
    }

    onConfirm(command, params, label);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-md bg-app-surface border border-app-border shadow-2xl rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border bg-app-bg/50">
          <div className="flex items-center gap-2">
            {isHedge ? (
              <Shield size={20} weight="bold" className="text-[#38BDF8]" />
            ) : isBuy ? (
              <TrendUp size={20} weight="bold" className="text-[#10B981]" />
            ) : isSell ? (
              <TrendDown size={20} weight="bold" className="text-[#FF3B30]" />
            ) : (
              <Lightning size={20} weight="bold" className="text-app-accent" />
            )}
            <h3 className="font-display font-bold text-base text-app-text tracking-wide uppercase">
              {isHedge
                ? "CUSTOM LOT HEDGE"
                : isBuy
                ? "EXECUTE BUY ORDER"
                : isSell
                ? "EXECUTE SELL ORDER"
                : "CUSTOM LOT ORDER"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-app-muted hover:text-app-text p-1 rounded-md transition-colors"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Target Fleet */}
          <div className="flex items-center justify-between p-3 bg-app-bg rounded-lg border border-app-border">
            <span className="font-mono-d text-xs text-app-muted uppercase tracking-wider">Target Fleet</span>
            <span className="font-mono-d text-xs font-bold text-app-accent flex items-center gap-1.5">
              <Lightning size={13} weight="fill" />
              {selectedCount} Account{selectedCount === 1 ? "" : "s"} Selected
            </span>
          </div>

          {/* Action Mode Switcher */}
          <div>
            <label className="block font-mono-d text-[11px] uppercase tracking-wider text-app-subtle mb-1.5">
              Action Category
            </label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                type="button"
                onClick={() => setCommand("buy_order")}
                className={`py-2 px-3 text-xs font-mono-d font-bold rounded-lg border uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                  !isHedge
                    ? "bg-app-accent-soft border-app-accent text-app-accent shadow-sm"
                    : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                }`}
              >
                <Lightning size={14} weight="bold" /> Direct Order
              </button>
              <button
                type="button"
                onClick={() => setCommand("hedge_custom")}
                className={`py-2 px-3 text-xs font-mono-d font-bold rounded-lg border uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                  isHedge
                    ? "bg-[#38BDF8]/15 border-[#38BDF8] text-[#38BDF8] shadow-sm"
                    : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                }`}
              >
                <Shield size={14} weight="bold" /> Custom Hedge
              </button>
            </div>

            {/* Type / Direction Selection */}
            {!isHedge ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCommand("buy_order")}
                  className={`py-2 px-3 text-xs font-mono-d font-bold rounded-lg border uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                    isBuy
                      ? "bg-[#10B981]/15 border-[#10B981] text-[#10B981] shadow-sm"
                      : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                  }`}
                >
                  <TrendUp size={14} weight="bold" /> BUY ORDER
                </button>
                <button
                  type="button"
                  onClick={() => setCommand("sell_order")}
                  className={`py-2 px-3 text-xs font-mono-d font-bold rounded-lg border uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                    isSell
                      ? "bg-[#FF3B30]/15 border-[#FF3B30] text-[#FF3B30] shadow-sm"
                      : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                  }`}
                >
                  <TrendDown size={14} weight="bold" /> SELL ORDER
                </button>
              </div>
            ) : (
              <div>
                <label className="block font-mono-d text-[10px] uppercase tracking-wider text-app-subtle mb-1">
                  Hedge Direction
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setHedgeSide("auto")}
                    className={`py-1.5 px-2 text-xs font-mono-d font-bold rounded border uppercase tracking-wider flex items-center justify-center gap-1 transition-all ${
                      hedgeSide === "auto"
                        ? "bg-[#38BDF8]/20 border-[#38BDF8] text-[#38BDF8]"
                        : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                    }`}
                  >
                    Auto Net
                  </button>
                  <button
                    type="button"
                    onClick={() => setHedgeSide("buy")}
                    className={`py-1.5 px-2 text-xs font-mono-d font-bold rounded border uppercase tracking-wider flex items-center justify-center gap-1 transition-all ${
                      hedgeSide === "buy"
                        ? "bg-[#10B981]/20 border-[#10B981] text-[#10B981]"
                        : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                    }`}
                  >
                    <TrendUp size={13} weight="bold" /> Buy Hedge
                  </button>
                  <button
                    type="button"
                    onClick={() => setHedgeSide("sell")}
                    className={`py-1.5 px-2 text-xs font-mono-d font-bold rounded border uppercase tracking-wider flex items-center justify-center gap-1 transition-all ${
                      hedgeSide === "sell"
                        ? "bg-[#FF3B30]/20 border-[#FF3B30] text-[#FF3B30]"
                        : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                    }`}
                  >
                    <TrendDown size={13} weight="bold" /> Sell Hedge
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Lot Size Selection */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="font-mono-d text-[11px] uppercase tracking-wider text-app-subtle">
                Lot Selection
              </label>
              <span className="font-mono-d text-[11px] text-app-accent font-semibold">
                {lot} Lot{parseFloat(lot) === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid grid-cols-6 gap-1.5 mb-2">
              {LOT_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setLot(p.toString())}
                  className={`py-1.5 text-xs font-mono-d rounded border transition-colors ${
                    parseFloat(lot) === p
                      ? "bg-app-accent-soft border-app-accent text-app-accent font-bold"
                      : "bg-app-bg border-app-border text-app-muted hover:text-app-text"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="100"
              value={lot}
              onChange={(e) => setLot(e.target.value)}
              placeholder="Custom lot (e.g. 0.03)"
              className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm font-mono-d rounded-lg focus:outline-none focus:border-app-accent"
              required
            />
          </div>

          {/* SL & TP Controls */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-mono-d text-[11px] uppercase tracking-wider text-app-subtle mb-1.5">
                Stop Loss (SL Pips)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={slPips}
                onChange={(e) => setSlPips(e.target.value)}
                placeholder="20"
                className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm font-mono-d rounded-lg focus:outline-none focus:border-app-accent"
              />
              <span className="block text-[10px] text-app-muted mt-1">0 = No SL (Pips from entry)</span>
            </div>

            <div>
              <label className="block font-mono-d text-[11px] uppercase tracking-wider text-app-subtle mb-1.5">
                Take Profit (TP Pips)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={tpPips}
                onChange={(e) => setTpPips(e.target.value)}
                placeholder="40"
                className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm font-mono-d rounded-lg focus:outline-none focus:border-app-accent"
              />
              <span className="block text-[10px] text-app-muted mt-1">0 = No TP (Pips from entry)</span>
            </div>
          </div>

          {/* Trailing Stop Loss */}
          <div>
            <label className="block font-mono-d text-[11px] uppercase tracking-wider text-app-subtle mb-1.5">
              Trailing Stop Loss (SL Trailing Pips)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              value={trailingSlPips}
              onChange={(e) => setTrailingSlPips(e.target.value)}
              placeholder="15"
              className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm font-mono-d rounded-lg focus:outline-none focus:border-app-accent"
            />
            <span className="block text-[10px] text-app-muted mt-1">0 = Disabled (Dynamic trailing SL in pips)</span>
          </div>

          {err && (
            <div className="p-2.5 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg font-mono-d text-xs text-[#FF3B30]">
              {err}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-app-border">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-xs font-mono-d uppercase tracking-wider border border-app-border bg-app-bg text-app-muted hover:text-app-text rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || selectedCount === 0}
              className={`px-5 py-2 text-xs font-mono-d font-bold uppercase tracking-wider text-white rounded-lg shadow-md transition-all flex items-center gap-1.5 ${
                isHedge
                  ? "bg-[#0284C7] hover:bg-[#0369A1]"
                  : isBuy
                  ? "bg-[#10B981] hover:bg-[#059669]"
                  : isSell
                  ? "bg-[#FF3B30] hover:bg-[#DC2626]"
                  : "bg-app-accent hover:opacity-90"
              } disabled:opacity-50`}
            >
              <Check size={14} weight="bold" />
              {busy
                ? "Sending..."
                : isHedge
                ? `Confirm HEDGE (${lot} Lot${parseFloat(lot) === 1 ? "" : "s"})`
                : `Confirm ${isBuy ? "BUY" : isSell ? "SELL" : "ORDER"}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
